<template>
  <div ref="mapContainer" class="map-container" :style="{ height }"></div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  defineProps,
  defineEmits,
} from "vue";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const props = defineProps({
  lat: {
    type: Number,
    default: null,
  },
  lng: {
    type: Number,
    default: null,
  },
  zoom: {
    type: Number,
    default: 13,
  },
  height: {
    type: String,
    default: "300px",
  },
});

const emit = defineEmits(["update:coordinates"]);

const DEFAULT_CENTER = [23.8813, 55.1694]; // Lithuania

const mapContainer = ref(null);
let map = null;
let marker = null;

const currentCenter = () => {
  if (props.lat != null && props.lng != null) {
    return [props.lng, props.lat];
  }
  return DEFAULT_CENTER;
};

const placeMarker = (lngLat) => {
  if (!marker) {
    marker = new maplibregl.Marker({ draggable: true })
      .setLngLat(lngLat)
      .addTo(map);
    marker.on("dragend", () => {
      const { lat, lng } = marker.getLngLat();
      emit("update:coordinates", { lat, lng });
    });
  } else {
    marker.setLngLat(lngLat);
  }
};

onMounted(() => {
  map = new maplibregl.Map({
    container: mapContainer.value,
    style: "https://tiles.openfreemap.org/styles/bright",
    center: currentCenter(),
    zoom: props.lat != null && props.lng != null ? props.zoom : 6,
  });

  map.addControl(new maplibregl.NavigationControl(), "top-right");

  if (props.lat != null && props.lng != null) {
    placeMarker({ lat: props.lat, lng: props.lng });
  }

  map.on("click", (e) => {
    placeMarker(e.lngLat);
    emit("update:coordinates", { lat: e.lngLat.lat, lng: e.lngLat.lng });
  });
});

onBeforeUnmount(() => {
  map?.remove();
});

watch(
  () => [props.lat, props.lng],
  ([lat, lng]) => {
    if (lat == null || lng == null || !map) return;

    const current = marker?.getLngLat();
    if (current && current.lat === lat && current.lng === lng) return;

    placeMarker({ lat, lng });
    map.easeTo({
      center: [lng, lat],
      zoom: Math.max(map.getZoom(), props.zoom),
    });
  },
);
</script>

<style scoped>
.map-container {
  width: 100%;
}
</style>
